import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import Reveal from "./Reveal";
import { useAuth } from "@/contexts/AuthContext";
import { resolveCtaTarget } from "@/lib/ctaTarget";
import { PLANS, CURRENCY, PRICING_ASSURANCE } from "@/lib/plans";

export const Pricing = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const ctaTarget = resolveCtaTarget(user);
    const [annual, setAnnual] = useState(false);
    const [hovered, setHovered] = useState(null);

    return (
        <section className="section" data-testid="pricing-section">
            <div className="container-mx">
                <Reveal>
                    <div className="text-center">
                        <h2 className="font-display font-extrabold text-text-primary" style={{ fontSize: "clamp(36px, 5vw, 56px)", letterSpacing: "-0.025em" }}>
                            Прості тарифи <span className="text-secondary-c">для будь-якого бізнесу.</span>
                        </h2>
                        <p className="mt-4 text-lg text-secondary-c">
                            Оберіть план — через 20 хвилин реклама вже налаштована.
                        </p>
                    </div>
                </Reveal>

                {/* Monthly / Annual toggle */}
                <Reveal delay={0.1}>
                    <div className="mt-10 flex items-center justify-center gap-3">
                        <button
                            onClick={() => setAnnual(false)}
                            data-testid="pricing-toggle-monthly"
                            className={`px-5 py-2 rounded-full text-sm font-medium transition ${!annual ? "bg-surface-hover text-text-primary border border-primary/40" : "text-secondary-c"}`}
                        >
                            Місяць
                        </button>
                        <button
                            onClick={() => setAnnual(true)}
                            data-testid="pricing-toggle-yearly"
                            className={`px-5 py-2 rounded-full text-sm font-medium transition flex items-center gap-2 ${annual ? "bg-surface-hover text-text-primary border border-primary/40" : "text-secondary-c"}`}
                        >
                            Рік
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-success/15 text-success-c">−25%</span>
                        </button>
                    </div>
                </Reveal>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {PLANS.map((p, i) => {
                        const price = annual ? p.annual : p.monthly;
                        const dim = hovered !== null && hovered !== i;
                        return (
                            <Reveal key={p.key} delay={i * 0.08}>
                                <div
                                    data-testid={`pricing-card-${p.key}`}
                                    onMouseEnter={() => setHovered(i)}
                                    onMouseLeave={() => setHovered(null)}
                                    className={`relative glass rounded-2xl p-7 h-full transition-all duration-300 ${p.featured ? "scale-[1.02]" : ""}`}
                                    style={{
                                        borderColor: p.featured ? "rgba(109,94,248,0.45)" : undefined,
                                        boxShadow: p.featured ? "0 0 32px rgba(109,94,248,0.18)" : undefined,
                                        filter: dim ? "blur(1.5px)" : "none",
                                        opacity: dim ? 0.55 : 1,
                                    }}
                                >
                                    {p.featured && (
                                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-medium text-white" style={{ background: "var(--gradient-1)" }}>
                                            Найпопулярніший
                                        </span>
                                    )}
                                    <h3 className="font-display font-bold text-2xl text-text-primary">{p.name}</h3>
                                    <p className="mt-1 text-sm text-secondary-c">{p.tagline}</p>

                                    <div className="mt-6 flex items-baseline gap-1.5 h-[64px] perspective-[1000px]">
                                        <AnimatePresence mode="wait">
                                            <motion.div
                                                key={annual ? "a" : "m"}
                                                initial={{ rotateX: 90, opacity: 0 }}
                                                animate={{ rotateX: 0, opacity: 1 }}
                                                exit={{ rotateX: -90, opacity: 0 }}
                                                transition={{ duration: 0.4 }}
                                                className="flex items-baseline gap-1.5"
                                                style={{ transformStyle: "preserve-3d" }}
                                            >
                                                <span className="font-display font-extrabold text-text-primary" style={{ fontSize: "52px", letterSpacing: "-0.03em" }}>
                                                    {CURRENCY}{price}
                                                </span>
                                                <span className="text-secondary-c text-sm">/міс</span>
                                            </motion.div>
                                        </AnimatePresence>
                                    </div>

                                    <ul className="mt-6 space-y-3">
                                        {p.highlights.map((h, j) => (
                                            <li key={j} className="flex gap-2.5 items-start text-sm">
                                                <Check size={16} className={`mt-0.5 shrink-0 ${h.upgrade ? "text-primary-c" : "text-success-c"}`} />
                                                <span className="text-text-primary/90">{h.text}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        onClick={() => navigate(ctaTarget)}
                                        data-testid={`pricing-select-${p.key}`}
                                        className={`mt-7 w-full ${p.featured ? "btn-primary pulse-glow" : "btn-ghost"}`}
                                    >
                                        Почати з {p.name}
                                    </button>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>

                <Reveal delay={0.3}>
                    <p className="mt-8 text-center text-sm text-secondary-c">
                        {PRICING_ASSURANCE}
                    </p>
                </Reveal>
            </div>
        </section>
    );
};

export default Pricing;